import React, { useState } from "react";
import {
  ChatHistoryItem,
  sendChatMessage,
} from "../../services/chatApiService";

type Message = {
  id: number;
  text: string;
  sender: "user" | "bot";
};

const renderMessageText = (text: string) => {
  const parts = text.split(/(https?:\/\/[^\s<]+)/g);

  return parts.map((part, index) => {
    if (!/^https?:\/\//i.test(part)) {
      return part;
    }

    const trailingPunctuation = part.match(/[),.;!?]+$/)?.[0] || "";
    const url = trailingPunctuation
      ? part.slice(0, -trailingPunctuation.length)
      : part;

    return (
      <React.Fragment key={`${url}-${index}`}>
        <a href={url} target="_blank" rel="noreferrer">
          {url}
        </a>
        {trailingPunctuation}
      </React.Fragment>
    );
  });
};

const ChatBotWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      sender: "bot",
      text: "Hi! I’m your app assistant. Ask me a question and I’ll send it to the backend for an answer.",
    },
  ]);

  const handleSend = async () => {
    const trimmed = draft.trim();
    if (!trimmed || isSending) {
      return;
    }

    const userMessage: Message = {
      id: Date.now(),
      text: trimmed,
      sender: "user",
    };

    setMessages((previous) => [...previous, userMessage]);
    setDraft("");
    setIsSending(true);

    try {
      const history: ChatHistoryItem[] = messages.slice(-6).map(
        (item): ChatHistoryItem => ({
          role: item.sender === "user" ? "user" : "assistant",
          content: item.text,
        }),
      );

      const reply = await sendChatMessage(trimmed, history);

      setMessages((previous) => [
        ...previous,
        {
          id: Date.now() + 1,
          text: reply,
          sender: "bot",
        },
      ]);
    } catch (error: any) {
      setMessages((previous) => [
        ...previous,
        {
          id: Date.now() + 2,
          text:
            error?.message ||
            "I couldn’t reach the backend right now. Please try again in a moment.",
          sender: "bot",
        },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void handleSend();
    }
  };

  return (
    <div className="chatbot-widget">
      {isOpen && (
        <div
          className="chatbot-panel"
          role="dialog"
          aria-label="Assistant chat"
        >
          <div className="chatbot-header">
            <div>
              <strong>Assistant</strong>
              <small>Live help</small>
            </div>
            <button
              type="button"
              className="chatbot-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close assistant"
            >
              ×
            </button>
          </div>

          <div className="chatbot-body" aria-live="polite">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`chatbot-message ${message.sender}`}
              >
                {renderMessageText(message.text)}
              </div>
            ))}
            {isSending && <div className="chatbot-message bot">Thinking…</div>}
          </div>

          <div className="chatbot-input-row">
            <label htmlFor="chatbot-message" className="sr-only">
              Message
            </label>
            <textarea
              id="chatbot-message"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={onKeyDown}
              rows={2}
              placeholder="Ask about leads, jobs, repairs..."
              disabled={isSending}
            />
            <button
              type="button"
              onClick={() => void handleSend()}
              disabled={isSending}
            >
              {isSending ? "Sending..." : "Send"}
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        className="chatbot-toggle"
        aria-label="Open assistant"
        onClick={() => setIsOpen((open) => !open)}
      >
        💬
      </button>
    </div>
  );
};

export default ChatBotWidget;
